import React, { type ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider as OidcProvider } from 'react-oidc-context'
import { AuthProvider } from './shell/AuthContext'
import { PermissionsProvider } from './shell/PermissionsContext'
import { AuthGate } from './app/AuthGate'
import { InactivityWarning } from './app/InactivityWarning'
import { App } from './app/App'
import { createQueryClient } from './app/queryClient'
import { initTheme } from './design/theme-store'
import { ToastProvider } from './design/Toast'
import { OIDC_AUTHORITY, OIDC_CLIENT_ID } from './env'
import './index.css'
import { rememberReturnTo, safeReturnTo } from './shell/returnTo'

/**
 * The standalone bootstrap — the portal served on its own, replacing Relay.Web.Server.
 *
 * Everything here is composition; the behaviour lives in components that have their own tests.
 * This file is on coverage-exclusions.txt because importing it performs the mount.
 *
 * **One way in.** Every person signs in at the identity provider (Plans/identity-consolidation.md);
 * `AuthGate` redirects there, and the provider's own pages — dressed as this portal's — collect the
 * password, the second factor, or hand off to an organization's own identity provider.
 */

const queryClient = createQueryClient()

const oidcConfig = {
  authority: OIDC_AUTHORITY,
  client_id: OIDC_CLIENT_ID,
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid email profile',
  // Silent renew, which the Blazor client never had: its whole session story was "a 401 sends you
  // back to /login".
  automaticSilentRenew: true,
}

initTheme()

// Everything below the session: identical either way, which is the point of AuthContextValue.
const portal: ReactNode = (
  <PermissionsProvider>
    <BrowserRouter>
      <App />
      {/* Standalone only. Federated, the host owns the session and its timeout. */}
      <InactivityWarning />
    </BrowserRouter>
  </PermissionsProvider>
)

const session: ReactNode = OIDC_AUTHORITY ? (
  <OidcProvider
    {...oidcConfig}
    onSigninCallback={(user) => {
      // Drop the OIDC response params from the address bar so a refresh does not replay them, and remember where the
      // person was going: the /callback route takes them there (shell/returnTo.ts).
      rememberReturnTo(safeReturnTo(user?.state))
      window.history.replaceState({}, document.title, window.location.pathname)
    }}
  >
    <AuthProvider>
      <AuthGate>{portal}</AuthGate>
    </AuthProvider>
  </OidcProvider>
) : (
  <p role="alert" style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
    This portal has no identity provider configured (OIDC_AUTHORITY), so nobody can sign in.
  </p>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>{session}</ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
